import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup } from "@angular/forms";
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from "@angular/router";
import { Store, select } from "@ngrx/store";
import { NGXLogger } from "ngx-logger";
import { async, Subject, Subscription } from "rxjs";
import { filter, take, takeLast, takeUntil, withLatestFrom } from "rxjs/operators";
import { Asset } from "src/app/models/assets.model";
import { Chapter } from "src/app/models/chapters.model";
import { Question } from "src/app/models/question.model";
import { AssetsService } from "src/app/services/assets.service";
import { EdisonFedertedLogin } from "src/app/services/auth-service/base.token.provider";
import { ChaptersService } from "src/app/services/chapters.service";
import { commonTokenProvider } from "src/main";
import { SignInState } from "../../../../app/components/allusers/signin/signin.model";
import { getSignedInUser } from "../../../../app/components/allusers/signin/signin.selector";
import { Quiz } from "../../../../app/models/quiz.model";
import { AnswerOptionsService } from "../../../../app/services/answer-options.service";
import { MemberlocService } from "../../../../app/services/memberloc.service";
import { ProgressService } from "../../../../app/services/progress.service";
import { environment } from "../../../../environments/environment";
import { CertificatesService } from "../../../services/certificates.service";
import { QuestionsService } from "../../../services/questions.service";
import { QuizesService } from "../../../services/quizes.service";
import { CourseState } from "../courses/courses.reducer";
import { isCourseNotSelected, getSelectedCourseState } from "../courses/courses.selector";
import {
  AssetShowAction,
  AudioShowAction,
  FinalNotAllowedQuizIncompleteAction,
  FinalQuizAlreadyDoneAction,
  FinalQuizLoadedAction,
  FinalQuizShowAction,
  FinalQuizSubmittedAction,
  FinalQuizSubmittingAction,
  InitializationAction,
  QuizLoadedAction,
  QuizShowAction,
  QuizSubmittedAction,
  QuizSubmittingAction,
  QuizTestsActionTypes,
  VideoShowAction
} from "./quiz-tests.actions";
import { QuizTestsState } from "./quiz-tests.model";
import {
  getCurrentQuizTestsState,
  isAsset,
  isFinal,
  isFinalNotAllowedQuizIncomplete,
  isFinalShow,
  isQuiz,
  isQuizShow,
  isQuizSkippingNotAllowed
} from "./quiz-tests.selector";
import { Course } from "src/app/models/course.model";

@Component({
  selector: "app-quiz-tests",
  templateUrl: "./quiz-tests.component.html",
  styleUrls: ["./quiz-tests.component.css"],
})
export class QuizTestsComponent implements OnInit, OnDestroy {
  /** Optionally provided by the parent (ChapterViewComponent already fetches this), to avoid a duplicate fetch. */
  @Input() progresses: any[] = null;

  destroy$ = new Subject<void>();
  chapterId: string;
  quizId;
  videoId: string;
  soundId: string;
  assetId: string;

  currentState$ = this.quizTestsStore.pipe(select(getCurrentQuizTestsState));
  currentState: QuizTestsState;

  currentUser$ = this.loginStore.pipe(select(getSignedInUser),
    filter(val => val !== null && val !== undefined));
  currentUser;

  currentCourse: CourseState;

  activatedRoute$ = this.activatedRoute.params.pipe(
    withLatestFrom(this.courseStore.pipe(select(getSelectedCourseState))),
    filter(([_, course]) => course !== null && course !== undefined)
  );
  
  isFinalNotAllowedQuizIncomplete$ = this.quizTestsStore.pipe(
    select(isFinalNotAllowedQuizIncomplete)
  );
  isQuizSkippingNotAllowed$ = this.quizTestsStore.pipe(
    select(isQuizSkippingNotAllowed)
  );

  chapter: Chapter;
  quiz: Quiz;
  asset: Asset;
  quizForm: FormGroup = new FormGroup({});
  result: any;

  
  quizzesPerChapter: Quiz[];

  isQuizShow$ = this.quizTestsStore
      .pipe(
        select(isQuizShow),
        filter((val) => (val ? true : false))
      );
  
  isFinalShow$ = this.quizTestsStore
      .pipe(
        select(isFinalShow),
        filter((val) => (val ? true : false))
      );

  isAsset$ = this.quizTestsStore
      .pipe(
        select(isAsset),
        filter((val) => (val ? true : false))
      );
  
  subscriptions: Subscription[] = [];

  constructor(
    private logger: NGXLogger,
    private chapterService: ChaptersService,
    private quizesService: QuizesService,
    private questionsService: QuestionsService,
    private answerOptionsService: AnswerOptionsService,
    private certificatesService: CertificatesService,
    public router: Router,
    private courseStore: Store<CourseState>,
    private quizTestsStore: Store<QuizTestsState>,
    private loginStore: Store<SignInState>,
    private activatedRoute: ActivatedRoute,
    private progressService: ProgressService,
    private memberLocService: MemberlocService,
    private assetsService: AssetsService,
    private sanitizer: DomSanitizer,
  ) { 
    this.logger.debug(`QuizTestsComponent constructor`);
  }

  async ngOnInit() {
    this.logger.debug(`QuizTestsComponent initialized`);
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach(subscription => subscription.unsubscribe());
      this.subscriptions = [];
    }

    this.subscriptions.push(this.currentState$.subscribe(val => {      
      this.currentState = val;
    }));

    this.subscriptions.push(this.currentUser$.subscribe(val => {
      this.currentUser = val;
    }));

    this.subscriptions.push(this.activatedRoute$.subscribe(([val, _]) => {
      this.currentCourse = _;
      this.chapterId = val.chapterId;
      this.quizId = val.quizId; 
      this.videoId = val.videoId;
      this.soundId = val.soundId;
      this.assetId = val.assetId;
      this.triggerState();
    }));

    this.subscriptions.push(this.isQuizShow$
      .subscribe(async (state: any) => {      
        this.logger.debug(`isQuizShow - ${state.currentState}`);
        this.showQuiz();
      }));

    this.subscriptions.push(this.isFinalShow$
      .subscribe(async (state) => {
        this.quizId = state.quizId ? state.quizId.toString() : null;
        await this.showFinal();
      }));

    this.subscriptions.push(this.isAsset$
      .subscribe(async (state) => {
        this.asset = await this.assetsService.findBy(state.assetId);
      }));   
  }

  ngOnDestroy() {    
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];  
    this.activatedRoute$ = null;
    this.currentUser$ = null;    
    this.currentState$ = null;
    this.isQuizShow$ = null;
    this.isFinalShow$ = null;
    this.isAsset$ = null;
    this.isFinalNotAllowedQuizIncomplete$ = null;
    this.isQuizSkippingNotAllowed$ = null;
    
    this.logger.debug(`QuizTestsComponent destroyed`);
  }

  async getSkippedQuizzes() {
    const skippedQuizes: Quiz[] = [];
    if (this.quizzesPerChapter && Array.isArray(this.quizzesPerChapter)) {
      this.quizzesPerChapter = this.quizzesPerChapter.sort((quizA, quizB) => {
        let aorder = Number.parseInt(quizA.QuizOrder);
        let border = Number.parseInt(quizB.QuizOrder);
        return aorder - border;
      });

      const index = this.quizzesPerChapter.findIndex(
        (q) => q.QuizID == parseInt(this.quizId)
      );
      const userProgresses = await this.getMemberProgress();
      for (let i = 0; i < index; i++) {
        if (
          !userProgresses.find(
            (p) => p.QuizID == this.quizzesPerChapter[i].QuizID && Number(p.Score) >= 60
          )
        ) {
          skippedQuizes.push(this.quizzesPerChapter[i]);
        }
      }
    }
    return skippedQuizes;
  }

  async triggerState() {
    this.logger.info(`Triggering state - ${this.currentState?.currentState} - chapterId: ${this.chapterId} - quizId: ${this.quizId}`);
    if (this.chapterId === "final") {
      this.quizTestsStore.dispatch(
        new FinalQuizShowAction({
          currentState: QuizTestsActionTypes.FinalQuizShow,
          quizId: this.currentState?.finalQuiz?.QuizID,
        } as QuizTestsState)
      );
    } else if (this.quizId) {
      const quizzes = await this.quizesService
        .findByCourseIdAndChapterId(this.currentCourse.course.id, parseInt(this.chapterId));
      this.quizzesPerChapter = quizzes;
      this.chapter = await this.chapterService.getChapterBy(this.currentCourse.course.id, parseInt(this.chapterId));

      const skippedQuizzes = await this.getSkippedQuizzes();
      if (skippedQuizzes && skippedQuizzes.length == 0) {
        this.quizTestsStore.dispatch(
          new QuizShowAction({
            currentState: QuizTestsActionTypes.QuizShow,
            quizId: parseInt(this.quizId),
          } as QuizTestsState)
        );
      } else {
        this.quizTestsStore.dispatch(
          new QuizShowAction({
            currentState: QuizTestsActionTypes.QuizSkipped,
            quizId: parseInt(this.quizId),
            incompleteQuizzes: skippedQuizzes,
          } as QuizTestsState)
        );
      }
    } else if (this.soundId) {
      this.quizTestsStore.dispatch(
        new AudioShowAction({
          currentState: QuizTestsActionTypes.AudioShow,
          soundId: this.soundId,
        } as QuizTestsState)
      );
    } else if (this.videoId) {
      this.quizTestsStore.dispatch(
        new VideoShowAction({
          currentState: QuizTestsActionTypes.VideoShow,
          videoId: this.videoId,
        } as QuizTestsState)
      );
    } else if (this.assetId) {
      this.quizTestsStore.dispatch(
        new AssetShowAction({
          currentState: QuizTestsActionTypes.AssetShow,
          assetId: this.assetId,
        } as QuizTestsState)
      );
    } else {
      this.quizTestsStore.dispatch(
        new InitializationAction({
          currentState: QuizTestsActionTypes.Initialization          
        } as QuizTestsState)
      );
    }
  }

  async getQuizData(quizId: number) {
    await this.updateMemberLocation();

    this.logger.info(`Getting quiz data for quizId: ${quizId}`);
    this.quiz = await this.quizesService.findByCourseIdAndQuizId(this.currentCourse.course.id, quizId);
    if (!this.quiz) {
      this.logger.error(`Quiz not found for quizId: ${quizId} in course ${this.currentCourse.course.id}`);
      return;
    }    
    const chapterId = parseInt(this.quiz.QuizLocation2);
    const questions = await this.questionsService.findByQuiz(this.currentCourse.course.id, chapterId, this.quiz.QuizID, this.quiz.QuestionLimit > 0 ? true : false);
    this.quiz = { ...this.quiz, questions };

    const answerOptions = await this.answerOptionsService.findByQuiz(
      this.currentCourse.course.id, chapterId, quizId
    );

    for (const question of questions) {
      if (!question.answerOptions) {
        question.answerOptions = [];
      }
      const filteredOptions = answerOptions.filter(
        (o) => o.QuestionID === question.QuestionID
      );
      question.answerOptions.push(...filteredOptions);
    }

    this.quizForm = await this.toFormGroup();
  }

  async toFormGroup() {
    const group: any = {};

    for (const question of this.quiz.questions) {
      const answer = {
        AnswerOptID: new FormControl(""),
      };
      group[question.QuestionID] = new FormGroup(answer);
    }

    return new FormGroup(group);
  }

  getSound() {
    return `${environment.s3BucketUrl}assets/sounds/${this.soundId}`;
  }

  getVideo() {
    return `${environment.s3BucketUrl}assets/video/${this.videoId}`;
  }

  getImage(path) {
    return `${environment.s3BucketUrl}assets${path}`;
  }

  getOptionLetter(index: number) {
    return String.fromCharCode(97 + index);
  }

  getAssett() {
    return `${environment.s3BucketUrl}/assets/${this.assetId}`;
  }

  public async submitQuiz() {
    if (this.quizId !== this.currentState.finalQuiz?.QuizID.toString()) {
      this.quizTestsStore.dispatch(
        new QuizSubmittingAction({
          currentState: QuizTestsActionTypes.QuizSubmitting,
          quizId: parseInt(this.quizId),
        } as QuizTestsState)
      );
      const result = await this.submitProgress();
      this.quizTestsStore.dispatch(
        new QuizSubmittedAction({
          currentState: QuizTestsActionTypes.QuizSubmitted,
          quizId: parseInt(this.quizId),
          result,
        } as QuizTestsState)
      );
    } else {
      this.quizTestsStore.dispatch(
        new FinalQuizSubmittingAction({
          currentState: QuizTestsActionTypes.FinalQuizSubmitting,
          quizId: this.currentState.finalQuiz?.QuizID,
        } as QuizTestsState)
      );
      const result = await this.submitProgress();      
      this.quizTestsStore.dispatch(
        new FinalQuizSubmittedAction({
          currentState: QuizTestsActionTypes.FinalQuizSubmitted,
          quizId: this.currentState.finalQuiz?.QuizID,
          result,
        } as QuizTestsState)
      );
    }
  }

  private async submitProgress() {
    try {
      const data = Object.create({});
      data[this.quizId] = this.quizForm.value;      
      const edisonFederatedLogin = commonTokenProvider?.federatedLogin as EdisonFedertedLogin;
      if (edisonFederatedLogin && edisonFederatedLogin?.edisonDomain && edisonFederatedLogin?.edisonRefreshToken) {
        return this.progressService.memberProgressByCourse(this.currentCourse.course.id,
          this.currentUser.memberid, data,
          { edisonDomain: edisonFederatedLogin?.edisonDomain, edisonRefreshToken: edisonFederatedLogin?.edisonRefreshToken });
      }

      console.log(`Submitting progress for quizId: ${this.quizId}`);
      return this.progressService.memberProgressByCourse(this.currentCourse.course.id, this.currentUser.memberid, data);
    } catch (error) {
      console.log(error);
    }
  }

  getAnswer(question: Question) {
    const que = this.quiz.questions.find(
      (q) => q.QuestionID == question.QuestionID
    );
    return que.answerOptions.find((option) => option.Correct === "yes")
      .AnswerText;
  }

  retakeQuiz() {
    this.quizTestsStore.dispatch(
      new QuizShowAction({
        currentState: QuizTestsActionTypes.QuizShow,
        quizId: parseInt(this.quizId),
      } as QuizTestsState)
    );
  }

  async showQuiz() {              
      await this.getQuizData(parseInt(this.quizId));
      this.quizTestsStore.dispatch(
        new QuizLoadedAction({
          currentState: QuizTestsActionTypes.QuizLoaded,
        quizId: parseInt(this.quizId),
      } as QuizTestsState)
    );
  }

  /** Fetches the member's progress for the course once, reusing the parent's input if provided. */
  async getMemberProgress(): Promise<any[]> {
    if (!this.progresses) {
      this.progresses = await this.progressService.getProgressByCourse(
        this.currentCourse.course.id,
        this.currentUser.memberid
      );
    }
    return this.progresses;
  }

  async showFinal() {
    try {
      const progresses = await this.getMemberProgress();

      this.logger.info(`Checking if final is already done...`);
      const isFinalAlreayDone = this.isFinalAlreayDone(progresses);
      if (isFinalAlreayDone) {
        this.quizTestsStore.dispatch(
          new FinalQuizAlreadyDoneAction({
            currentState: QuizTestsActionTypes.FinalQuizAlreadyDone,
          } as QuizTestsState)
        );
        return;
      }

      this.logger.info(`Checking if there are incomplete quizzes...`);
      const incompleteQuizzes = await this.getIncompleteQuizzes(progresses);
      if (incompleteQuizzes && incompleteQuizzes.length) {
        this.quizTestsStore.dispatch(
          new FinalNotAllowedQuizIncompleteAction({
            currentState: QuizTestsActionTypes.FinalNotAllowedQuizIncomplete,
            incompleteQuizzes,
          } as QuizTestsState)
        );
        return;
      }

      this.logger.info(`Loading final quiz data...`);
      await this.getQuizData(this.currentState?.finalQuiz?.QuizID);
      this.logger.info(`Final quiz data loaded`, this.quiz);
      this.quizTestsStore.dispatch(
        new FinalQuizLoadedAction({
          currentState: QuizTestsActionTypes.FinalQuizLoaded,
          quizId: this.currentState?.finalQuiz?.QuizID,
        } as QuizTestsState)
      );
    } catch (err) {
      this.logger.error(err);
    }

  }

  private updateMemberLocation() {
    if (
      this.chapterId &&
      this.chapterId !== "final" &&
      parseInt(this.chapterId)
    ) {
      const chapter = parseInt(this.chapterId);
      return this.memberLocService.updateMemberLocationByCourse(
        this.currentCourse.course.id,
        this.currentUser.memberid,
        {
          memberid: this.currentUser.memberid,
          courseid: this.currentCourse.course.id,
          section: chapter.toString(),
          quid: this.quizId,
        }
      );
    }
    return this.memberLocService.updateMemberLocationByCourse(
      this.currentCourse.course.id,
      this.currentUser.memberid,
      {
        memberid: this.currentUser.memberid,
        courseid: this.currentCourse.course.id,
        section: this.chapterId,
        quid: this.quizId,
      }
    );
  }

  isFinalAlreayDone(progresses: any[]) {
    if (this.currentUser.memberstatus === "admin") {
      return false;
    }

    return progresses.find((p) => p.QuizID == this.currentState?.finalQuiz.QuizID);
  }

  async getIncompleteQuizzes(progresses: any[]) {
    let totalQuizes = await this.quizesService.findAllPerCourse(this.currentCourse.course.id);

    progresses = progresses.filter((val) =>
      val ? val && val.QuizID && val.Score >= 60 : false
    );
    // unique quizzes
    progresses = progresses.filter(
      (item, pos, self) =>
        self.findIndex((v) => v.QuizID === item.QuizID) === pos
    );
    totalQuizes = totalQuizes.filter((q) => {
      const progress = progresses.find((p) => p.QuizID === q.QuizID);
      if (progress) {
        return false;
      }
      return true;
    });
    return totalQuizes;
  }

  getIncompleteLink(quiz) {
    return "/courses/" + this.currentCourse.course.id + "/chapters/" + quiz.QuizLocation2 + "/quizzes/" + quiz.QuizID;
  }

  downloadCertificate() {
    this.router.navigate(["courses", this.currentCourse.course.id, "download-certificate"]);
  }

  getAssetUrl() {
    const assetUrl = `${environment.s3BucketUrl}${this.asset.location}`;
    return assetUrl;
  }

  getPptUrl() {
    return this.sanitizer.bypassSecurityTrustResourceUrl(`https://view.officeapps.live.com/op/embed.aspx?src=${environment.s3BucketUrl}${this.asset.location}`);
  }


}
